import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ArrowDownCircleFill from '@/assets/icons/arrow_down_circle_fill.svg?component';
import ChartLine from '@/assets/icons/chart_line.svg?component';
import HealingBandage from '@/assets/icons/healing_bandage.svg?component';
import Warning from '@/assets/icons/warning.svg?component';
import ContentBlock from '@/components/molecules/ContentBlock';
import { OverviewValueWithTrendTooltip } from '@/components/molecules/OverviewValueWithTrendTooltip';
import { WoundDescription } from '@/components/molecules/WoundDescription';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { FullSizeImage } from '@/domain/images';
import { formatPercentageByLocale, type parseLocale } from '@/utils/localeFormatters';

import type { HealthWoundsInsightDisplayData } from '../_utils/healthWoundsInsightDisplay';
import HealthWoundDistributionCard from '../components/HealthWoundDistributionCard/HealthWoundDistributionCard';
import HealthWoundInsightDetections from '../components/HealthWoundInsightDetections/HealthWoundInsightDetections';
import HealthWoundsInsightInformationPanel from '../components/HealthWoundsInsightInformationPanel/HealthWoundsInsightInformationPanel';
import HealthWoundsMetricCard from '../components/HealthWoundsMetricCard/HealthWoundsMetricCard';

import styles from './HealthWoundsBodyInsightView.module.scss';

export interface HealthWoundsBodyInsightViewProps {
    isLoading: boolean;
    insight: HealthWoundsInsightDisplayData | null;
    /** False for a species the estimate does not suit, or while the pen's species is unknown. */
    showDowngradeEstimate: boolean;
    imagesData: FullSizeImage[];
    cageId: number;
    timezone: string;
    isOptoScaleAdmin: boolean;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
    locale: ReturnType<typeof parseLocale>;
}

export default function HealthWoundsBodyInsightView(props: HealthWoundsBodyInsightViewProps) {
    const {
        isLoading,
        insight,
        showDowngradeEstimate,
        imagesData,
        cageId,
        timezone,
        isOptoScaleAdmin,
        onCommitFalseDetection,
        locale,
    } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    const totalWoundsValue =
        insight?.totalWoundsRate != null && !Number.isNaN(insight.totalWoundsRate) ? (
            <OverviewValueWithTrendTooltip
                value={insight.totalWoundsRate}
                growth={insight.totalWoundsSlope}
                showPercentage
                className={styles.totalWoundsTrend}
            />
        ) : (
            formatPercentageByLocale(null, locale)
        );
    const acuteValue = formatPercentageByLocale(insight?.acutePercent, locale);
    const healingValue = formatPercentageByLocale(insight?.healingPercent, locale);
    const downGradeValue = formatPercentageByLocale(insight?.downGradeEstimatePercent, locale);

    return (
        <div className={styles.insightLayout}>
            <div
                className={classNames(styles.insightPageGrid, {
                    [styles.insightPageGridNoQuality]: !showDowngradeEstimate,
                })}>
                <ContentBlock spacing="none" shrink className={styles.metricOne}>
                    <HealthWoundsMetricCard
                        isLoading={isLoading}
                        title={t('wounds.totalWounds')}
                        value={totalWoundsValue}
                        icon={ChartLine}
                        tone="info"
                        iconCustomSize={24}
                    />
                </ContentBlock>
                <ContentBlock spacing="none" shrink className={styles.metricTwo}>
                    <HealthWoundsMetricCard
                        isLoading={isLoading}
                        title={t('wounds.acute')}
                        value={acuteValue}
                        icon={Warning}
                        tone="danger"
                    />
                </ContentBlock>
                <ContentBlock spacing="none" shrink className={styles.metricThree}>
                    <HealthWoundsMetricCard
                        isLoading={isLoading}
                        title={t('wounds.healing')}
                        value={healingValue}
                        icon={HealingBandage}
                        tone="success"
                        iconCustomSize={24}
                    />
                </ContentBlock>
                {showDowngradeEstimate && (
                    <ContentBlock spacing="none" shrink className={styles.qualityCell}>
                        <HealthWoundsMetricCard
                            isLoading={isLoading}
                            title={tGlobal('common.labels.estimatedDowngrade')}
                            value={downGradeValue}
                            icon={ArrowDownCircleFill}
                            tone="danger"
                            tooltipContent={tGlobal('harvestReport.common.estDowngradeTooltip')}
                            tooltipWidth={450}
                        />
                    </ContentBlock>
                )}

                <div className={styles.mainStack}>
                    <ContentBlock
                        title={t('wounds.woundDescription')}
                        className={styles.woundDescriptionCell}
                        spacing="none"
                        shrink>
                        {insight ? (
                            <div className={styles.woundDescriptionContent}>
                                <WoundDescription
                                    showPieChart
                                    layout="stacked"
                                    woundsColor={insight.woundDescription}
                                />
                            </div>
                        ) : null}
                    </ContentBlock>
                    <ContentBlock
                        title={t('wounds.woundDistribution')}
                        className={styles.woundDistributionCell}
                        spacing="none"
                        shrink>
                        <HealthWoundDistributionCard
                            isLoading={isLoading}
                            noWoundPercent={insight?.noWoundPercent}
                            oneWoundPercent={insight?.oneWoundPercent}
                            moreThanOneWoundPercent={insight?.moreThanOneWoundPercent}
                        />
                    </ContentBlock>
                    <div className={styles.detectionsCell}>
                        <HealthWoundInsightDetections
                            isLoading={isLoading}
                            imagesData={imagesData}
                            cageId={cageId}
                            timezone={timezone}
                            isOptoScaleAdmin={isOptoScaleAdmin}
                            onCommitFalseDetection={onCommitFalseDetection}
                        />
                    </div>
                </div>

                <div className={styles.informationCell}>
                    <HealthWoundsInsightInformationPanel
                        isLoading={isLoading}
                        fishCount={insight?.fishCount ?? null}
                        lastMeasurement={insight?.lastMeasurement ?? ''}
                    />
                </div>
            </div>
        </div>
    );
}
